import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  X, Calendar, Clock, Video, Banknote, User as UserIcon, Share2,
  CheckCircle2, XCircle, Download, CalendarPlus,
} from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { AttendeesSection } from './EventDetailsModal';
import { parseUtc } from '../lib/calendarDate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Audit Fable5 Jul 7 2026 (Bug 7) — panneau latéral de détails ouvert au clic
// sur une card de la liste Mes sessions. Cohérent avec le drawer du calendrier
// (EventDetailsModal variant="drawer") : mêmes couleurs, même animation.
// Brief Jul 27 2026 §5 — contenu adapté au rôle : invité (Accepter/Décliner),
// inscrit confirmé (Rejoindre + .ics), bookmarké (Réserver), expert (inchangé).
const STATUS_LABEL = {
  pending: { label: 'En attente de validation', cls: 'bg-amber-500/15 text-amber-300 border border-amber-400/30' },
  pending_payment: { label: 'Paiement en attente', cls: 'bg-blue-500/15 text-blue-300 border border-blue-400/30' },
  confirmed: { label: 'Confirmée', cls: 'bg-emerald-500/15 text-emerald-300 border border-emerald-400/30' },
  completed: { label: 'Terminée', cls: 'bg-white/10 text-gray-300 border border-white/15' },
  cancelled: { label: 'Annulée', cls: 'bg-red-500/15 text-red-300 border border-red-400/30' },
  declined: { label: 'Refusée', cls: 'bg-red-500/15 text-red-300 border border-red-400/30' },
  published: { label: 'Publié', cls: 'bg-indigo-500/15 text-indigo-300 border border-indigo-400/30' },
  draft: { label: 'Brouillon', cls: 'bg-amber-500/15 text-amber-300 border border-amber-400/30' },
};

const parseStart = (row) => {
  if (!row?.start_time) return null;
  if (typeof row.start_time === 'string') {
    // Fix timezone Jul 2026 — backend datetimes are naive UTC: parseUtc.
    if (row.start_time.includes('T')) return parseUtc(row.start_time);
    if (row.date) return parseUtc(`${row.date}T${row.start_time}:00`);
    return null;
  }
  return parseUtc(row.start_time);
};

const SessionDetailsDrawer = ({ row, isOpen, onClose, currentUserId, onShare, onAction }) => {
  const navigate = useNavigate();
  if (!isOpen || !row) return null;

  const start = parseStart(row);
  const cp = row.counterpart || {};
  const isBooking = row.kind === 'booking';
  const isOwnedEvent = row.kind === 'owned_event';
  const isInvitation = row.kind === 'invitation';
  const isBookmark = row.kind === 'bookmarked_event';
  const isExpertSide = (isBooking && row.viewer_role === 'expert') || isOwnedEvent;
  const status = STATUS_LABEL[row.status] || { label: row.status || '—', cls: 'bg-white/10 text-gray-300 border border-white/15' };
  const title = row.title || row.session_type?.title || (isBooking ? `Session avec ${cp.display_name || '…'}` : 'Session');
  const priceNum = Number(row.price) || 0;
  const isPaid = !row.is_free && priceNum > 0;
  const isFuture = start && start.getTime() > Date.now();
  // Brief Aug 10 2026 — session en cours maintenant.
  const isLive = start && !['cancelled', 'completed', 'declined', 'draft'].includes(row.status)
    && Date.now() >= start.getTime()
    && Date.now() <= start.getTime() + (Number(row.duration) || 60) * 60000;
  // Visio : expert/owner toujours ; apprenant seulement une fois confirmé.
  const canJoin = !!row.visio_link && (isExpertSide || (isBooking && row.viewer_role === 'client' && row.status === 'confirmed'));
  const roleLabel = isExpertSide ? 'Expert'
    : isInvitation ? 'Invité'
    : isBookmark ? 'Enregistré'
    : row.viewer_role === 'client' ? 'Apprenant' : row.viewer_role || '';
  // Ajouter au calendrier — inscrit confirmé (event public ou 1-to-1 legacy).
  const icsUrl = row.event_id
    ? `${API}/calendar/events/${row.event_id}/ics`
    : row.booking_id ? `${API}/bookings/${row.booking_id}/ical` : null;

  // Brief Jul 27 2026 §1 — l'expert qui rejoint déclenche la notification
  // "session démarrée" aux participants (one-shot, fenêtre gérée côté backend).
  const pingSessionStarted = () => {
    if (isExpertSide && row.event_id) {
      axios.post(`${API}/calendar/events/${row.event_id}/session-started`, {}, { withCredentials: true }).catch(() => {});
    }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="session-details-drawer">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-gradient-to-b from-[#0d1f35] via-[#0f2540] to-[#0d1f35] border-l border-white/10 shadow-2xl p-6 overflow-y-auto os-drawer-slide-in">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-bold text-white leading-snug" data-testid="session-details-title">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="session-details-close"
            className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white shrink-0"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 mt-3">
          {isLive && (
            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-semibold bg-red-500/15 text-red-300 border border-red-400/30" data-testid="session-details-live-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> En cours
            </span>
          )}
          <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${status.cls}`} data-testid="session-details-status">
            {status.label}
          </span>
          {isPaid ? (
            <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium bg-purple-500/15 text-purple-300 border border-purple-400/30">
              <Banknote className="h-3 w-3" /> {priceNum} {row.currency || 'CHF'}
            </span>
          ) : (
            <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-white/10 text-gray-300 border border-white/15">Gratuit</span>
          )}
          <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-cyan-500/15 text-cyan-300 border border-cyan-400/30">
            {roleLabel}
          </span>
        </div>

        <div className="space-y-2.5 text-sm text-gray-300 mt-5">
          {start && (
            <p className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-cyan-300 shrink-0" />
              {start.toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          )}
          {start && (
            <p className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-cyan-300 shrink-0" />
              {start.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })}
              {row.duration ? ` · ${row.duration} min` : ''}
            </p>
          )}
          {(isBooking || isInvitation) && cp.display_name && (
            <div className="flex items-center gap-2.5 p-2.5 rounded-lg bg-white/5 border border-white/10" data-testid="session-details-counterpart">
              <Avatar className="h-8 w-8">
                <AvatarImage src={cp.avatar} />
                <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-pink-500 text-white text-xs">
                  {(cp.display_name || '?').split(' ').map((n) => n[0]).join('').substring(0, 2)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="text-xs text-gray-400">{isExpertSide ? 'Participant' : 'Avec'}</p>
                <p className="text-sm text-white font-medium truncate">{cp.display_name}</p>
              </div>
            </div>
          )}
          {row.description && (
            <p className="text-xs text-gray-400 whitespace-pre-line">{row.description}</p>
          )}
        </div>

        {/* Brief Jul 27 2026 §5 — invité (invitation en attente) : Accepter/Décliner */}
        {isInvitation && row.status === 'pending' && onAction && (
          <div className="mt-5 flex gap-2">
            <button
              type="button"
              onClick={() => onAction('invitation', row.event_id, 'accept')}
              data-testid="session-details-accept-invitation"
              className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold"
            >
              <CheckCircle2 className="h-4 w-4" /> Accepter
            </button>
            <button
              type="button"
              onClick={() => onAction('invitation', row.event_id, 'decline')}
              data-testid="session-details-decline-invitation"
              className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-red-400/40 text-red-300 text-sm font-semibold hover:bg-red-500/10"
            >
              <XCircle className="h-4 w-4" /> Décliner
            </button>
          </div>
        )}

        {/* Brief Jul 27 2026 §5 — bookmarké : CTA Réserver si futur */}
        {isBookmark && isFuture && row.event_id && (
          <button
            type="button"
            onClick={() => { onClose(); navigate(`/event/${row.event_id}?book=1`); }}
            data-testid="session-details-book"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 text-white text-sm font-semibold hover:opacity-90"
          >
            <CalendarPlus className="h-4 w-4" /> Réserver ma place
          </button>
        )}

        {/* Lien visio — expert toujours, apprenant seulement si confirmé */}
        {canJoin && (
          <a
            href={row.visio_link}
            target="_blank"
            rel="noreferrer"
            onClick={pingSessionStarted}
            data-testid="session-details-join"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold hover:opacity-90"
          >
            <Video className="h-4 w-4" /> Rejoindre la visio
          </a>
        )}
        {/* Inscrit confirmé — Ajouter au calendrier (.ics) */}
        {isBooking && row.viewer_role === 'client' && row.status === 'confirmed' && icsUrl && (
          <a
            href={icsUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="session-details-ics"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-gray-200 text-xs hover:bg-white/10"
          >
            <Download className="h-3.5 w-3.5" /> Ajouter au calendrier (.ics)
          </a>
        )}
        {isBooking && row.viewer_role === 'client' && row.status !== 'confirmed' && (
          <p className="mt-5 text-xs text-gray-500 flex items-center gap-1.5" data-testid="session-details-join-locked">
            <Video className="h-3.5 w-3.5" />
            Le lien visio sera disponible une fois ta réservation confirmée.
          </p>
        )}

        {onShare && (
          <button
            type="button"
            onClick={() => onShare(row)}
            data-testid="session-details-share"
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-violet-500/10 border border-violet-400/30 text-violet-200 text-xs hover:bg-violet-500/20"
          >
            <Share2 className="h-3.5 w-3.5" /> Partager
          </button>
        )}

        {/* Participants + statuts paiement — côté expert uniquement */}
        {isExpertSide && row.event_id && (
          <AttendeesSection eventId={row.event_id} />
        )}
        {isExpertSide && !row.event_id && (
          <p className="mt-5 pt-4 border-t border-white/10 text-xs text-gray-500 flex items-center gap-1.5">
            <UserIcon className="h-3.5 w-3.5" />
            Session 1-to-1 — un seul participant.
          </p>
        )}
      </div>
    </div>
  );
};

export default SessionDetailsDrawer;
